import type { IntegrationConfig } from "../types";
import { callOpenAiChat, type OpenAiMessage, type OpenAiToolDef } from "../providers/supabase/aiApi";
import { describeAnthropicError } from "./providerErrors";
import { DEFAULT_ANTHROPIC_MODEL, DEFAULT_OPENAI_MODEL } from "./models";
import type { AiToolDef } from "./tools";

export interface ByoAiMessage {
  role: "user" | "assistant";
  text: string;
}

export interface ByoAiToolSupport {
  tools: AiToolDef[];
  executeTool: (name: string, input: Record<string, unknown>) => Promise<string>;
}

const MAX_TOOL_ROUNDS = 4;
const TOO_MANY_ROUNDS = "I made too many tool calls in a row - try rephrasing your request.";

function safeParseJson(json: string): Record<string, unknown> {
  try {
    return JSON.parse(json || "{}");
  } catch {
    return {};
  }
}

async function callAnthropic(
  apiKey: string,
  model: string,
  system: string,
  seed: ByoAiMessage[],
  toolSupport: ByoAiToolSupport | undefined,
): Promise<{ ok: boolean; text: string }> {
  // Anthropic's own message shape, not our simplified ByoAiMessage - a tool
  // round trip needs to carry the assistant's raw content blocks back in.
  let messages: unknown[] = seed.map((m) => ({ role: m.role, content: m.text }));
  const tools = toolSupport?.tools.map((t) => ({
    name: t.name,
    description: t.description,
    input_schema: t.parameters,
  }));

  for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
    let res: Response;
    try {
      res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model,
          max_tokens: 1024,
          system,
          messages,
          ...(tools?.length ? { tools } : {}),
        }),
      });
    } catch (e) {
      return {
        ok: false,
        text: `Couldn't reach the AI provider (${e instanceof Error ? e.message : "network error"}). If you're in the preview, the sandbox may block outbound calls - this works from your deployed workspace.`,
      };
    }
    if (!res.ok) {
      return { ok: false, text: describeAnthropicError(res.status) };
    }

    const data = await res.json();
    const content: { type: string; text?: string; id?: string; name?: string; input?: unknown }[] =
      data?.content ?? [];
    const toolUses = content.filter((b) => b.type === "tool_use");

    if (toolUses.length === 0 || !toolSupport) {
      const text = content.map((b) => b.text ?? "").join("") || "(empty response)";
      return { ok: true, text };
    }

    const results = await Promise.all(
      toolUses.map(async (tu) => ({
        type: "tool_result",
        tool_use_id: tu.id,
        content: await toolSupport.executeTool(
          tu.name!,
          (tu.input as Record<string, unknown>) ?? {},
        ),
      })),
    );
    messages = [...messages, { role: "assistant", content }, { role: "user", content: results }];
  }
  return { ok: false, text: TOO_MANY_ROUNDS };
}

async function callOpenAiWithTools(
  apiKey: string,
  model: string,
  system: string,
  seed: ByoAiMessage[],
  toolSupport: ByoAiToolSupport | undefined,
): Promise<{ ok: boolean; text: string }> {
  let messages: OpenAiMessage[] = [
    { role: "system", content: system },
    ...seed.map((m): OpenAiMessage => ({ role: m.role, content: m.text })),
  ];
  const tools: OpenAiToolDef[] | undefined = toolSupport?.tools.map((t) => ({
    type: "function",
    function: { name: t.name, description: t.description, parameters: t.parameters },
  }));

  for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
    let result;
    try {
      result = await callOpenAiChat({ apiKey, model, messages, tools });
    } catch (e) {
      return { ok: false, text: e instanceof Error ? e.message : "Couldn't reach OpenAI." };
    }

    if (!result.toolCalls?.length || !toolSupport) {
      return { ok: true, text: result.content ?? "(empty response)" };
    }

    const toolResultMessages: OpenAiMessage[] = await Promise.all(
      result.toolCalls.map(async (tc) => ({
        role: "tool" as const,
        tool_call_id: tc.id,
        content: await toolSupport.executeTool(tc.function.name, safeParseJson(tc.function.arguments)),
      })),
    );
    messages = [
      ...messages,
      { role: "assistant", content: result.content, tool_calls: result.toolCalls },
      ...toolResultMessages,
    ];
  }
  return { ok: false, text: TOO_MANY_ROUNDS };
}

/**
 * Shared bring-your-own-key AI call - the one place both BYO-AI surfaces
 * (AiAssistant.tsx's Ask AI chat, ScorecardTab.tsx's narrative generator)
 * reach a provider from. Anthropic keys work browser-direct (their API
 * allows CORS-enabled access); OpenAI routes through apps/services'
 * relay (it blocks direct browser calls). Never duplicate this logic at
 * a call site - that's how the two surfaces drifted out of sync before.
 *
 * `toolSupport` is optional - ScorecardTab's one-shot narrative never
 * passes it, so tools are never even sent for that call.
 */
export const callByoAi = async (
  ai: IntegrationConfig | undefined,
  system: string,
  messages: ByoAiMessage[],
  toolSupport?: ByoAiToolSupport,
): Promise<{ ok: boolean; text: string }> => {
  const fields = ai?.fields ?? {};
  const apiKey = (fields.apiKey ?? "").trim();
  const provider = fields.provider || "anthropic";
  if (!apiKey) {
    return {
      ok: false,
      text: "No AI key connected - add one under Integrations → AI.",
    };
  }

  if (provider === "openai") {
    return callOpenAiWithTools(
      apiKey,
      fields.model?.trim() || DEFAULT_OPENAI_MODEL,
      system,
      messages,
      toolSupport,
    );
  }
  return callAnthropic(apiKey, fields.model?.trim() || DEFAULT_ANTHROPIC_MODEL, system, messages, toolSupport);
};
